import { useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { staffAPI } from '../services/api';

const NAVIGATION_RANKING_STALE_MS = 6 * 60 * 60 * 1000;

/**
 * Fetch the signed-in administrator's daily-stable ranking and record route
 * transitions. Telemetry failures stay silent and never block navigation.
 */
export const useAdminNavigationAnalytics = ({
  currentItemKey,
  userIdentifier,
  enabled = true,
}) => {
  const rankingQuery = useQuery({
    queryKey: ['navigation', 'admin', 'ranking', userIdentifier],
    queryFn: async () => {
      const response = await staffAPI.getAdminNavigationRanking();
      return response.data || null;
    },
    enabled,
    staleTime: NAVIGATION_RANKING_STALE_MS,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (error?.response?.status === 403) return false;
      return failureCount < 1;
    },
  });

  const { mutate: recordVisit } = useMutation({
    mutationFn: (itemKey) => staffAPI.recordAdminNavigationVisit(itemKey),
    retry: false,
  });
  const lastRecordedItemRef = useRef(null);

  useEffect(() => {
    const visitIdentity = userIdentifier && currentItemKey
      ? `${userIdentifier}:${currentItemKey}`
      : null;
    if (
      !enabled ||
      !visitIdentity ||
      lastRecordedItemRef.current === visitIdentity
    ) {
      return;
    }

    lastRecordedItemRef.current = visitIdentity;
    recordVisit(currentItemKey);
  }, [currentItemKey, enabled, recordVisit, userIdentifier]);

  return rankingQuery;
};

export default useAdminNavigationAnalytics;
